/**
 * ChatGPT Ads (OpenAI) conversion pixel — the only place we report conversions.
 *
 * The loader and `oaiq("init", …)` live in index.html; this module owns every
 * `measure` call, so what the ad account counts can be audited in one file
 * instead of being scattered across pages.
 *
 * Contract, taken from the SDK itself (bzrcdn.openai.com/sdk/oaiq.min.js):
 *
 *   oaiq("measure", <event name>, eventProps, eventOptions)
 *
 *   - The verb is `measure`. There is no `track` — the SDK's command switch
 *     accepts only init / consent / measure / measureSingle, and anything else
 *     is silently dropped, which is exactly the failure you never notice.
 *   - Event names are a closed set of documented measurement events. An
 *     unknown name is dropped with an `unsupported_event_name` diagnostic, so
 *     names live in PIXEL_EVENTS here rather than as strings at call sites.
 *   - `eventProps.type` must match the event's data type — `customer_action`
 *     for registration_completed. Omitting it still sends, but logs a
 *     validation warning, so we always pass it.
 *   - `eventOptions.event_id` is the de-duplication key. Signups are keyed on
 *     the new user's id, so a re-render, a replayed navigation, or a later
 *     server-side Conversions API event for the same signup all collapse into
 *     one conversion rather than inflating the count.
 *
 * Nothing here throws. An ad pixel must never be able to break signup: every
 * call is wrapped, and a missing/blocked SDK simply reports nothing.
 */

/** Documented measurement events we send. */
export const PIXEL_EVENTS = {
  /** Customer created an account. */
  SIGNUP: 'registration_completed',
};

/** Marks the signup we have already reported, so a reload cannot double-count. */
const SIGNUP_FIRED_KEY = 'defera.oaiq.signup_fired';

/**
 * An account created this recently is a signup rather than a returning login.
 *
 * Only the OAuth path needs this: Google/Apple sign-in and sign-up come back
 * through the same callback with the same shape, so the only way to tell a new
 * customer from a returning one is how long ago the account was created.
 */
const NEW_USER_WINDOW_MS = 2 * 60 * 1000;

function readStorage(key) {
  try {
    return window.localStorage?.getItem(key) ?? null;
  } catch {
    // Private mode / blocked storage. Fall through — `event_id` still de-dupes.
    return null;
  }
}

function writeStorage(key, value) {
  try {
    window.localStorage?.setItem(key, value);
  } catch {
    /* non-fatal: see readStorage */
  }
}

/**
 * True when this user record was created moments ago (i.e. they just signed
 * up), false for a returning customer signing in again.
 *
 * Prefers the gap between account creation and this sign-in, which is stable
 * regardless of when we happen to run, and falls back to the account's age
 * when the provider gives us no sign-in timestamp.
 *
 * @param {{ id?: string, created_at?: string, createdAt?: string, last_sign_in_at?: string }} user
 * @param {number} [now] Injected for tests.
 */
export function isNewlyCreatedUser(user, now = Date.now()) {
  const createdAt = Date.parse(user?.created_at ?? user?.createdAt ?? '');
  if (!Number.isFinite(createdAt)) return false;

  const lastSignInAt = Date.parse(user?.last_sign_in_at ?? '');
  if (Number.isFinite(lastSignInAt)) {
    return lastSignInAt - createdAt < NEW_USER_WINDOW_MS;
  }
  return now - createdAt < NEW_USER_WINDOW_MS;
}

/**
 * Send one measurement event. Returns whether it reached the SDK queue, which
 * is all the browser can know — delivery itself is the SDK's business.
 *
 * @param {string} eventName One of PIXEL_EVENTS.
 * @param {Record<string, unknown>} [eventProps]
 * @param {Record<string, unknown>} [eventOptions]
 */
export function trackPixelEvent(eventName, eventProps, eventOptions) {
  try {
    if (typeof window === 'undefined' || typeof window.oaiq !== 'function') return false;
    window.oaiq('measure', eventName, eventProps, eventOptions);
    return true;
  } catch (err) {
    console.warn('[chatgpt-ads] conversion event failed (non-fatal)', err);
    return false;
  }
}

/**
 * Report a completed signup. Safe to call more than once for the same user —
 * the second call is a no-op locally, and `event_id` covers the case where
 * local storage is unavailable.
 *
 * @param {{ id?: string }} user The newly created user.
 */
export function trackSignupConversion(user) {
  const userId = user?.id;
  if (!userId) return false;
  if (readStorage(SIGNUP_FIRED_KEY) === String(userId)) return false;

  const sent = trackPixelEvent(
    PIXEL_EVENTS.SIGNUP,
    { type: 'customer_action' },
    { event_id: `signup_${userId}` },
  );
  if (sent) writeStorage(SIGNUP_FIRED_KEY, String(userId));
  return sent;
}
